import {Injectable} from '@angular/core';
import {environment} from '../../../environments/environment';
import {HttpClient} from '@angular/common/http';
import {PageRequest} from '../../shared/models/page-request.interface';
import {Observable, throwError} from 'rxjs';
import {Page} from '../../shared/models/page.interface';
import {KfRegistration} from '../../shared/models/kf-registration.interface';
import {KfRegistrationSearch} from '../../shared/models/kf-registration-search.interface';
import {ApiHttpResponse} from '../../shared/models/api-http-response.interface';
import {KfRegistrationDetail} from '../../kf-data-entry/models/kf-registration-detail.interface';
import {catchError, map} from 'rxjs/operators';

const KF_REGISTRATION_API = environment.apiUrl + 'kfregistration';

@Injectable({
  providedIn: 'root'
})
export class KfRegistrationAdminService {

  constructor(private http: HttpClient) {
  }

  static buildSearchParams(search: KfRegistrationSearch): string {
    let searchParams = '';
    if (search.startYear && search.startYear !== '') {
      searchParams += `&startYear=${search.startYear}`;
    }
    if (search.endYear && search.endYear !== '') {
      searchParams += `&endYear=${search.endYear}`;
    }
    if (search.entry && search.entry !== '') {
      searchParams += `&entry=${search.entry}`;
    }
    if (search.countryId) {
      searchParams += `&countryId=${search.countryId}`;
    }
    if (search.weeeSystemId) {
      searchParams += `&weeeSystemId=${search.weeeSystemId}`;
    }
    if (search.keyFigures && search.keyFigures !== '') {
      searchParams += `&keyFigures=${search.keyFigures}`;
    }
    if (search.b2bb2c && search.b2bb2c !== '') {
      searchParams += `&b2bb2c=${search.b2bb2c}`;
    }
    if (search.status && search.status !== '') {
      searchParams += `&status=${search.status}`;
    }
    if (search.schemaKind && search.schemaKind !== '') {
      searchParams += `&schemaKind=${search.schemaKind}`;
    }
    return searchParams;
  }

  static getSortCode(sort: string): string {
    switch (sort) {
      case 'open':
        return 'isReportingAvailable';
      default:
        return sort;
    }
  }


  getAllKfRegistrations(view: string, search: KfRegistrationSearch, pageRequest: PageRequest = {page: 0, size: 100}):
    Observable<Page<KfRegistration>> {
    let url = `${KF_REGISTRATION_API}?size=${pageRequest.size}&page=${pageRequest.page}`;
    if (view === 'validation' && search.status !== 'confidential' && search.status !== 'completed') {
      search.status = 'CO';
    }
    const searchParams = KfRegistrationAdminService.buildSearchParams(search);
    if (pageRequest.sort && pageRequest.direction) {
      url = url + `&sort=${KfRegistrationAdminService.getSortCode(pageRequest.sort)},${pageRequest.direction}`;
    }
    return this.http.get<Page<KfRegistration>>(url + searchParams);
  }

  get(keyFigures: string, referenceId: string): Observable<ApiHttpResponse<KfRegistration>> {
    return this.http.get<ApiHttpResponse<KfRegistration>>(`${KF_REGISTRATION_API}/${keyFigures}/${referenceId}`);
  }

  getKfRegistration(keyFigures: string, year: number, type: string, elementId?: number): Observable<KfRegistrationDetail> {
    return this.http.get<KfRegistrationDetail>(`${KF_REGISTRATION_API}/${keyFigures}?year=${year}&elementId=${elementId}&type=${type}`);
  }

  validate(keyFigures: string, year: number, type: string, elementId?: number): Observable<ApiHttpResponse<object>> {
    const fd = new FormData();
    fd.append('keyFigures', keyFigures);
    fd.append('year', year.toString());
    fd.append('elementId', elementId?.toString() || '');
    fd.append('type', type);
    return this.http.post<ApiHttpResponse<object>>(`${KF_REGISTRATION_API}/validate`, fd).pipe(
      catchError(err => {
        return throwError(err);
      }),
      map((res: ApiHttpResponse<object>) => {
        return res;
      })
    );
  }

  reopen(keyFigures: string, year: number, type: string, elementId?: number): Observable<ApiHttpResponse<object>> {
    const fd = new FormData();
    fd.append('keyFigures', keyFigures);
    fd.append('year', year.toString());
    fd.append('elementId', elementId?.toString() || '');
    fd.append('type', type);
    return this.http.post<ApiHttpResponse<object>>(`${KF_REGISTRATION_API}/reopen`, fd).pipe(
      catchError(err => {
        return throwError(err);
      }),
      map((res: ApiHttpResponse<object>) => {
        return res;
      })
    );
  }

  markAsNotValidable(keyFigures: string, year: number, type: string, elementId?: number): Observable<ApiHttpResponse<object>> {
    const fd = new FormData();
    fd.append('keyFigures', keyFigures);
    fd.append('year', year.toString());
    fd.append('elementId', elementId?.toString() || '');
    fd.append('type', type);
    return this.http.post<ApiHttpResponse<object>>(`${KF_REGISTRATION_API}/mark-not-validable`, fd).pipe(
      catchError(err => {
        return throwError(err);
      }),
      map((res: ApiHttpResponse<object>) => {
        return res;
      })
    );
  }

  existsPreviousYear(keyFigures: string, year: number, elementId: number, type: string): Observable<boolean> {
    return this.http.get<boolean>(`${KF_REGISTRATION_API}/${keyFigures}/exists-previous?year=${year}&elementId=${elementId}&type=${type}`);
  }

  printPdf(keyFigures: string, year: number, elementId: number, type: string): Observable<any> {
    return this.http.get(`${KF_REGISTRATION_API}/print/${keyFigures}?year=${year}&elementId=${elementId}&type=${type}`, {
      observe: 'response',
      responseType: 'blob'
    });
  }

}
